import { Vector3 } from "three";
import {
  refineCylinderGeometric,
  type GeometricCylinderFit,
} from "./cylinder-fit";
import type { CylinderSurface } from "./cylinder-surface";

export type CylinderFitter = (
  points: readonly Vector3[],
  initial: CylinderSurface,
  spacing: number,
  cancelled: () => boolean,
  depthPreview?: boolean,
) => Promise<GeometricCylinderFit>;

/**
 * @param points Owned fitting evidence; source geometry is never transferred here.
 * @param initial Provisional model or last accepted cylinder.
 * @param spacing Measured or nominal source spacing.
 * @param cancelled Current request/source generation check.
 * @param depthPreview Whether the input is a provisional raster-depth patch.
 * @returns Joint geometric fitting result; production supplies the worker adapter.
 */
export function fitCylinderInline(
  points: readonly Vector3[],
  initial: CylinderSurface,
  spacing: number,
  cancelled: () => boolean,
  depthPreview?: boolean,
): Promise<GeometricCylinderFit> {
  return refineCylinderGeometric(points, initial, {
    spacing,
    cancelled,
    depthPreview,
  });
}

export type SerializedCylinder = {
  center: number[];
  axis: number[];
  radial: number[];
  radius: number;
};
export type CylinderFitRequest = {
  id: number;
  positions: Float64Array;
  initial: SerializedCylinder;
  spacing: number;
  depthPreview?: boolean;
};
export type CylinderFitResponse = { id: number } & (
  | {
      result: Omit<GeometricCylinderFit, "cylinder"> & {
        cylinder: SerializedCylinder;
      };
    }
  | { error: string }
);

/**
 * @param cylinder World-space cylinder.
 * @returns Structured-clone-safe model.
 */
export function serializeCylinder(
  cylinder: CylinderSurface,
): SerializedCylinder {
  return {
    center: cylinder.center.toArray(),
    axis: cylinder.axis.toArray(),
    radial: cylinder.radial.toArray(),
    radius: cylinder.radius,
  };
}

/**
 * @param cylinder Worker model.
 * @returns Independent Three.js vectors.
 */
export function deserializeCylinder(
  cylinder: SerializedCylinder,
): CylinderSurface {
  return {
    center: new Vector3().fromArray(cylinder.center),
    axis: new Vector3().fromArray(cylinder.axis),
    radial: new Vector3().fromArray(cylinder.radial),
    radius: cylinder.radius,
  };
}
